/**
 * When an alarm marker gets a position, and when it loses one.
 *
 * An alarm row carries a TIME and no position, so its lat/lng comes from interpolating the boat
 * track. Doing that on every redraw is what made the markers drift: on a live map the track is a
 * SLIDING WINDOW, so re-interpolating a marker whose instant never moved still produced a different
 * answer as the window slid, and every pan re-ran the whole thing in screen space.
 *
 * The rule instead is: resolve once, keep while the track still covers the instant, drop when it
 * does not. These are the three decisions that rule needs, kept pure so they can be tested without
 * a Mapbox instance.
 */

/** Inclusive span of instants a track covers. */
export interface TrackWindow {
  startMs: number;
  endMs: number;
}

/** A marker that has been given a position. Only the fields the lifecycle cares about. */
export interface ResolvedMarkerLike {
  atMs: number;
}

/** A marker awaiting a position. Only the fields the lifecycle cares about. */
export interface PendingMarkerLike {
  id: string;
  atIso: string;
}

/** A marker as the source filter sees it. */
export interface SourcedMarkerLike {
  source: string;
}

/**
 * Keep only markers belonging to boats drawn on the map.
 *
 * The Alarms page and the map hold SEPARATE selections — the page lists the fleet's alarms, the map
 * draws the boats you asked for — so without this every alarm the page held got a marker, including
 * boats absent from the map. It also has to run on every pass, not just at resolve time: a marker
 * already given a position would otherwise sit there after its boat was deselected, because nothing
 * in the expiry rule mentions the source.
 *
 * A marker with an empty `source` comes from a single-source page, where the page IS the selection,
 * so it always passes. A null selection disables the filter entirely.
 */
export function filterMarkersToSelectedSources<T extends SourcedMarkerLike>(
  markers: readonly T[],
  selected: Iterable<string> | null | undefined
): T[] {
  if (selected == null) return [...markers];
  const allowed = new Set(
    [...selected].map((n) => String(n).trim().toUpperCase()).filter((n) => n !== '')
  );
  return markers.filter((m) => m.source === '' || allowed.has(m.source.trim().toUpperCase()));
}

/**
 * The span of instants the track covers, or null when it carries no usable timestamps.
 *
 * Null means "no opinion": with nothing to pin a marker to we neither resolve nor expire, so a
 * momentarily empty track cannot wipe markers that are still perfectly valid.
 */
export function computeTrackWindow(
  track: readonly unknown[],
  getTimestampMs: (row: unknown) => number
): TrackWindow | null {
  let startMs = Number.POSITIVE_INFINITY;
  let endMs = Number.NEGATIVE_INFINITY;
  for (const row of track) {
    const t = getTimestampMs(row);
    // <= 0 rejects the epoch fallback that the timestamp accessors return for an unparseable row.
    if (!Number.isFinite(t) || t <= 0) continue;
    if (t < startMs) startMs = t;
    if (t > endMs) endMs = t;
  }
  return endMs >= startMs ? { startMs, endMs } : null;
}

/** Is this instant still inside the window? A null window covers everything. */
export function isWithinWindow(atMs: number, window: TrackWindow | null): boolean {
  if (!window) return true;
  return atMs >= window.startMs && atMs <= window.endMs;
}

/**
 * Resolved markers that should be dropped: no longer present at all, or aged out of the window.
 *
 * "Aged out" is the deliberate removal the old layer never had — it only lost markers when
 * interpolation happened to fail, which also took out any marker sitting in an ordinary track gap.
 */
export function selectExpiredMarkerIds(
  resolved: ReadonlyMap<string, ResolvedMarkerLike>,
  liveIds: ReadonlySet<string>,
  window: TrackWindow | null
): string[] {
  const expired: string[] = [];
  for (const [id, r] of resolved) {
    if (!liveIds.has(id) || !isWithinWindow(r.atMs, window)) expired.push(id);
  }
  return expired;
}

/**
 * Markers that still need a position: in the window, and not already resolved.
 *
 * Returning only these is what makes the work proportional to what ARRIVED rather than to what is
 * displayed — the steady state, where every visible marker is already resolved, costs nothing.
 */
export function selectPendingMarkers<T extends PendingMarkerLike>(
  markers: readonly T[],
  resolvedIds: ReadonlySet<string>,
  window: TrackWindow | null
): T[] {
  const pending: T[] = [];
  for (const m of markers) {
    if (resolvedIds.has(m.id)) continue;
    const atMs = new Date(m.atIso).getTime();
    if (!Number.isFinite(atMs)) continue;
    if (!isWithinWindow(atMs, window)) continue;
    pending.push(m);
  }
  return pending;
}
